import styled from '@emotion/styled';
import kebabCase from 'lodash/kebabCase';
import PropTypes from 'prop-types';
import innerText from 'react-innertext';
import { useRef, useState } from 'react';

import Theme from '../../../themes/index';
import IconCheck from '../icons/icon-check';

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  color: ${props => (props.disabled ? '#666' : 'black')};
  flex-direction: ${props => (props.labelLeft ? 'row-reverse' : 'row')};
  justify-content: ${props => (props.labelLeft ? 'space-between' : 'flex-start')};
  opacity: ${props => (props.disabled ? '.3' : '1')};
  position: relative;

  &:hover {
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  }

  &:focus {
    outline-offset: ${props => (props.disabled ? '0' : '2px')};
    outline: ${props => (props.disabled ? 'none' : `${props.themeColor} solid 2px`)};
  }

  &:focus:not(:focus-visible) {
    outline: none !important;
  }

  &:focus-visible {
    outline-offset: ${props => (props.disabled ? '0' : '2px')};
    outline: ${props => (props.disabled ? 'none' : `${props.themeColor} solid 2px`)};
  }

  input[type='checkbox'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #ffffff;
    border: 1px solid #949494;
    cursor: pointer;
    flex-shrink: 0;
    height: 16px;
    margin: 2px 8px 0 0;
    width: 16px;
  }

  input[type='checkbox'] ~ .check {
    display: none;
    height: 9px;
    left: ${props => (props.labelLeft ? 'auto' : '2px')};
    position: absolute;
    top: 6px;
    width: 12px;

    ${props => props.labelLeft === true && `right: 10px;`}
  }

  input[type='checkbox']:checked ~ .check {
    pointer-events: none;
    display: inline-block;
  }

  input[type='checkbox']:checked {
    background-color: ${props => props.themeColor};
    border-color: ${props => (props.disabled ? '#666' : props.themeColor)};
  }
`;

const CheckboxLabel = styled.label`
  width: 100%;
`;

const Icon = styled(IconCheck)`
  position: absolute;
  left: 0;
  top: 0;
`;

const Checkbox = ({
  className,
  children,
  defaultChecked = false,
  checked,
  disabled = false,
  labelLeft = false,
  name,
  themeColor,
  onChange,
  value,
  id,
  ...props
}) => {
  if (!themeColor) {
    // eslint-disable-next-line no-param-reassign
    themeColor = Theme.colors.brand;
  }
  const checkBox = useRef();
  let setChecked = () => {};

  if (checked === undefined) {
    [checked, setChecked] = useState(defaultChecked);
  }

  const updatedChecked = event => {
    setChecked(!checked);
    onChange && onChange(event);
  };

  const checkboxId = id || `checkbox-${kebabCase([name, value].join(' ')) || kebabCase(innerText(children))}`;

  return (
    <CheckboxContainer className={className} disabled={disabled} themeColor={themeColor} labelLeft={labelLeft}>
      <input
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={updatedChecked}
        readOnly={disabled ? 'readonly' : null}
        ref={checkBox}
        tabIndex={disabled ? '-1' : '0'}
        type="checkbox"
        value={value}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      <CheckboxLabel htmlFor={checkboxId}>{children}</CheckboxLabel>
      <span className="check">
        <Icon fill="white" width="12px" />
      </span>
    </CheckboxContainer>
  );
};

export default Checkbox;

Checkbox.propTypes = {
  /** This is where you put the label of the checkbox */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Set this to true, if you want the checkbox disabled */
  disabled: PropTypes.bool,
  /** Call the function you want to fire when changed */
  onChange: PropTypes.func.isRequired,
  /** If you want the checkbox checked on load */
  defaultChecked: PropTypes.bool,
  /** If you want the label to be left and the check to the right */
  labelLeft: PropTypes.bool,
  /** Hex value for color theme */
  themeColor: PropTypes.string,
  /** Id of the checkbox input */
  id: PropTypes.string,
};

Checkbox.defaultProps = {
  disabled: false,
  defaultChecked: false,
  labelLeft: false,
  themeColor: null,
  id: null,
  children: null,
};
